import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { RankedBars } from '@/shared/ui/charts'
import { Pill, fmtRelative } from '@/features/dossier/components/primitives'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { getDashboard, listThreads, type Dashboard, type Thread } from './api'
import { ThreatFilter, useThreatBands } from './ThreatFilter'
import { Metric, PlatformChip, SentimentChip, StatusChip, ThreatScore, Velocity } from './components'

/**
 * The situation board — what is moving right now.
 *
 * ## Ranked by velocity, and it refreshes itself
 *
 * A war room is read while something is happening, so this polls. Thirty seconds, not two: the
 * radar itself runs on a minute and the approval step is a human reading a paragraph, so a
 * tighter refresh would buy nothing but a flickering screen.
 *
 * ## "Not read" is a first-class state
 *
 * A thread with no assessment shows as *not scored*, never as a zero. The whole failure mode
 * this board exists to prevent is a hostile thread going unnoticed because it looked harmless
 * on a dashboard that had never actually looked at it.
 */
export function SituationPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<Dashboard | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * The board, narrowed to one dossier subject.
   *
   * Without this the situation board and the threads feed showed the same rows and differed
   * only in layout — which is what made one of them feel redundant. Narrowed to a subject it
   * answers a different question: not "what is moving", but "what is moving about HIM", which
   * is the question somebody actually opens this screen with.
   *
   * The counts above stay deployment-wide on purpose. They are the room's workload — drafts
   * waiting, deployed today — and those do not become a different number because a reader is
   * looking at one candidate.
   */
  const [filterSubject, setFilterSubject] = useState('')
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [feed, setFeed] = useState<Thread[] | null>(null)
  const [query, setQuery] = useState('')
  const [draftQuery, setDraftQuery] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState({ pages: 1, elements: 0 })
  const [band, setBand] = useState('')
  const threatBands = useThreatBands()

  const load = useCallback(async () => {
    try {
      setError(null)
      const [dashboard, threads] = await Promise.all([
        getDashboard(),
        listThreads({
          q: query || undefined,
          subjectId: filterSubject || undefined,
          band: band || undefined,
          page,
          size: 25,
        }),
      ])
      setData(dashboard)
      setFeed(threads.content)
      setTotal({ pages: threads.totalPages, elements: threads.totalElements })
    } catch (err) {
      setError(explain(err))
    }
  }, [query, page, filterSubject, band])

  useEffect(() => {
    void load()
    void listSubjects({ size: '100' }).then(setSubjects).catch(() => setSubjects([]))
    const timer = setInterval(() => void load(), 30_000)
    return () => clearInterval(timer)
  }, [load])

  if (error && !data) return <ErrorStrip message={error} />
  if (!data) return <LoadingBlock label="Reading the room" />

  const rows = feed ?? data.topThreads
  // From the board's own count, NOT from the rows on screen. Counting the page said "25 of
  // the threads below" over a page that held 25, which was true, useless, and stopped moving.
  const unread = data.threadsUnscored

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Situation"
        description="What is being said about our candidates right now, fastest-moving first.
                     Nothing on this screen posts anything."
      />

      {error && <ErrorStrip message={error} />}

      <KpiStrip>
        <MetricCard label="Threads live" value={data.threadsLive} sub="Not dismissed" />
        <MetricCard label={`Threats at ${data.deployThreshold}+`}
                    value={data.threatsAboveThreshold}
                    sub="Scored high enough to answer" />
        <MetricCard label="Awaiting a human" value={data.draftsAwaiting}
                    sub="Drafts that cannot post themselves" />
        <MetricCard label="Deployed today" value={data.deployedToday}
                    sub="Each with a name against it" />
        <MetricCard label="Coordination" value={data.coordinationSignals}
                    sub="Repeated phrasing, unresolved" />
      </KpiStrip>

      {unread > 0 && (
        <p className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px]
                      text-amber-700 dark:text-amber-400">
          {unread} thread{unread === 1 ? ' has' : 's have'} no threat score. An unscored thread
          is not a harmless one — nothing has judged it yet, and opening it does not score it.
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <Panel>
          <div className="p-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] font-medium">
                {filterSubject
                  ? subjects.find((s) => s.id === filterSubject)?.displayName ?? 'Moving fastest'
                  : 'Moving fastest'}
              </p>
              <span className="text-[10px] text-muted-foreground">
                {total.elements} {total.elements === 1 ? 'thread' : 'threads'}
              </span>
            </div>

            {/* The same controls as the Threads tab — this list is the same data. */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <input value={draftQuery} onChange={(e) => setDraftQuery(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter') { setPage(0); setQuery(draftQuery) }
                     }}
                     placeholder="Search what was said…"
                     className="h-7 min-w-40 flex-1 rounded border border-input bg-background
                                px-2 text-[12px]" />
              <select value={filterSubject}
                      onChange={(e) => { setPage(0); setFilterSubject(e.target.value) }}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                <option value="">Every subject</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.displayName}</option>
                ))}
              </select>
              {query && (
                <button type="button"
                        onClick={() => { setDraftQuery(''); setQuery(''); setPage(0) }}
                        className="rounded border border-border px-2 py-1 text-[11px]">
                  Clear
                </button>
              )}
            </div>

            {/*
              Threat narrows on a different axis from search and subject — those choose which
              threads, this chooses how bad — so it gets its own row rather than becoming a
              third control in the strip above.
            */}
            <div className="mt-1.5">
              <ThreatFilter bands={threatBands} value={band}
                            onChange={(code) => { setPage(0); setBand(code) }} />
            </div>

            <ul className="mt-2 space-y-1.5">
              {rows.length === 0 && (
                <li className="py-8 text-center text-[12px] text-muted-foreground">
                  Nothing is being said about our candidates that the radar has found. Add a
                  watch, or capture a thread by hand.
                </li>
              )}
              {rows.map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => navigate(`/war-room/threads/${t.id}`)}
                          className="w-full rounded border border-border p-2 text-left
                                     hover:bg-muted/40">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="text-[12px] font-medium leading-snug">
                        {t.title ?? '(no text captured)'}
                      </span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <ThreatScore score={t.threatScore} threshold={data.deployThreshold} />
                        <SentimentChip code={t.sentimentCode} />
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <PlatformChip code={t.platformCode} />
                      {/*
                        Which subject, and a way through to everything about them. The card
                        itself opens the thread, so this stops the event — clicking the subject
                        means "show me all of it", not "open this one".
                      */}
                      {t.subjectId ? (
                        <span role="link" tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/war-room/subjects/${t.subjectId}`)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.stopPropagation()
                                  navigate(`/war-room/subjects/${t.subjectId}`)
                                }
                              }}
                              className="cursor-pointer rounded-full border border-sky-500/30
                                         bg-sky-500/10 px-2 py-0.5 text-[10px] text-sky-700
                                         hover:bg-sky-500/20 dark:text-sky-400">
                          {t.subjectName ?? 'unnamed subject'}
                        </span>
                      ) : (
                        <Pill className="border-border">unnamed subject</Pill>
                      )}
                      {t.authorHandle && (
                        <span className="text-[10px] text-muted-foreground">
                          @{t.authorHandle}
                        </span>
                      )}
                      <Velocity value={t.velocity} />
                      <Metric label="likes" value={t.likes} />
                      <Metric label="replies" value={t.replies} />
                      <span className="text-[10px] text-muted-foreground">
                        {fmtRelative(t.firstSeenAt)}
                      </span>
                      <StatusChip code={t.statusCode} />
                      {t.draftCount > 0 && (
                        <span className="text-[10px] text-muted-foreground">
                          {t.draftCount} draft{t.draftCount === 1 ? '' : 's'}
                        </span>
                      )}
                      {t.deployedCount > 0 && (
                        <span className="text-[10px] text-emerald-600">
                          {t.deployedCount} deployed
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            <OffsetPager page={page} totalPages={total.pages} totalElements={total.elements}
                         onPage={setPage} loading={false} unit="threads" />
          </div>
        </Panel>

        <Panel>
          <div className="p-2">
            <p className="text-[11px] font-medium">Where the pressure is</p>
            <p className="text-[10px] text-muted-foreground">
              Engagements per minute since we first saw each thread.
            </p>
            <RankedBars
              className="mt-2"
              points={data.topThreads
                .filter((t) => t.velocity != null)
                .slice(0, 10)
                .map((t) => ({
                  label: t.authorHandle ? `@${t.authorHandle}` : t.platformCode,
                  value: Number(t.velocity),
                }))}
              formatValue={(n) => `${n.toFixed(1)}/min`}
              emptyMessage="Nothing has a measured rate yet."
            />
          </div>
        </Panel>
      </div>
    </div>
  )
}
